import { SEARCH_MAX_REGISTERED_UNOBFUSCATED_ROWS } from '@shared/constants';
import { useMemo } from 'react';
import useFeature from '~/hooks/useFeature';
import { useUser } from '~/hooks/useUser';
import {
  MAX_DOWNLOAD_ROWS,
  SEARCH_MAX_GUEST_UNOBFUSCATED_ROWS,
} from '~/scripts/constants';
import {
  getDomainUploadLimit,
  getRowLimitFromPlanId,
  getUserLimitFromPlanId,
} from '~/scripts/planLimits';

interface PlanLimits {
  guestRows: number;
  registeredRows: number;
  downloadRows: number;
  users: number;
  domainUploads: number;
}
export const usePlanLimits = (): PlanLimits => {
  const user = useUser();

  const { guest, registered } = useObfuscatedRowsFeature();
  const planId = user.account.plans_id;

  const domainUploads = getDomainUploadLimit(planId);
  const planLimits: PlanLimits = {
    guestRows: guest,
    registeredRows: registered,
    downloadRows: MAX_DOWNLOAD_ROWS,
    users: 1,
    domainUploads,
  };

  if (user.isGuest || !user.isUpgraded) {
    return planLimits;
  }

  planLimits.downloadRows = getRowLimitFromPlanId(planId);
  planLimits.users = getUserLimitFromPlanId(planId);

  return planLimits;
};

function useObfuscatedRowsFeature() {
  const [obfuscatedRowsFeature, obfuscatedRowsVariables] =
    useFeature('obfuscated_rows');

  const { guest, registered } = useMemo(() => {
    if (!obfuscatedRowsFeature) {
      return {
        guest: SEARCH_MAX_GUEST_UNOBFUSCATED_ROWS,
        registered: SEARCH_MAX_REGISTERED_UNOBFUSCATED_ROWS,
      };
    }

    return {
      guest: +(
        obfuscatedRowsVariables?.guest || SEARCH_MAX_GUEST_UNOBFUSCATED_ROWS
      ),
      registered: +(
        obfuscatedRowsVariables?.registered ||
        SEARCH_MAX_REGISTERED_UNOBFUSCATED_ROWS
      ),
    };
  }, [obfuscatedRowsFeature, obfuscatedRowsVariables]);

  return { guest, registered };
}
